import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, LogOut, Search, Eye, Edit2, Trash2, X,
  AlertCircle, CheckCircle, ChevronDown, RefreshCw
} from 'lucide-react';
import { issuesApi } from '../../services/issuesApi';
import StatusBadge from '../issues/StatusBadge';
import '../issues/Issues.css';

const STATUSES = ['Open', 'In Progress', 'Under Review', 'Resolved', 'Closed'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

export default function AdminIssuesPanel({ onNavigate, onSelectIssue }) {
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [editModal, setEditModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  // Toast notification
  const [toast, setToast] = useState(null);
  
  // Inline editing
  const [inlineEdit, setInlineEdit] = useState(null);

  const token = localStorage.getItem('adminToken');

  // Navigation helper
  const navigate = useCallback((path) => {
    if (onNavigate) {
      if (path === '/admin/login') {
        onNavigate('issues-admin-login');
      } else if (path.startsWith('/issues/')) {
        const issueId = path.replace('/issues/', '');
        if (onSelectIssue) onSelectIssue(issueId);
        onNavigate('issues-detail');
      } else if (path === '/issues') {
        onNavigate('issues');
      }
    }
  }, [onNavigate, onSelectIssue]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [issuesData, statsData] = await Promise.all([
        issuesApi.getAdminIssues(token),
        issuesApi.getStats(token)
      ]);
      setIssues(issuesData.issues || []);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading admin data:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      if (onNavigate) onNavigate('issues-admin-login');
      return;
    }
    loadData();
  }, [token, onNavigate, loadData]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/admin/login');
  };

  const handleQuickStatusChange = async (issueId, newStatus) => {
    try {
      setInlineEdit(issueId);
      await issuesApi.updateStatus(issueId, newStatus, '', token);
      showToast(`Status updated to "${newStatus}"`);
      await loadData();
    } catch (err) {
      console.error('Error updating status:', err);
      showToast('Failed to update status', 'error');
    } finally {
      setInlineEdit(null);
    }
  };

  const handleQuickPriorityChange = async (issueId, newPriority) => {
    try {
      setInlineEdit(issueId);
      await issuesApi.updateIssue(issueId, { priority: newPriority }, token);
      showToast(`Priority updated to "${newPriority}"`);
      await loadData();
    } catch (err) {
      console.error('Error updating priority:', err);
      showToast('Failed to update priority', 'error');
    } finally {
      setInlineEdit(null);
    }
  };

  const handleUpdateIssue = async (issueId, updates) => {
    try {
      setUpdating(true);
      await issuesApi.updateIssue(issueId, updates, token);
      showToast('Issue updated successfully');
      await loadData();
      setEditModal(null);
    } catch (err) {
      console.error('Error updating issue:', err);
      showToast('Failed to update issue', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (issueId) => {
    try {
      setUpdating(true);
      await issuesApi.deleteIssue(issueId, token);
      showToast('Issue deleted successfully');
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting issue:', err);
      showToast('Failed to delete issue', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const filteredIssues = issues.filter(issue =>
    issue.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    issue.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    issue.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return '#dc2626';
      case 'High': return '#ea580c';
      case 'Medium': return '#ca8a04';
      case 'Low': return '#65a30d';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="empty-state" style={{ paddingTop: '4rem' }}>
          <div className="loading-spinner-small" style={{ margin: '0 auto' }} />
          <p>Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {/* Toast Notification */}
      {toast && (
        <div className={`admin-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <h1>
            <Shield size={24} />
            Issue Admin Panel
          </h1>
          <button className="admin-refresh-btn" onClick={loadData} title="Refresh">
            <RefreshCw size={18} />
          </button>
        </div>
        <button className="admin-logout-btn" onClick={handleLogout}>
          <LogOut size={18} />
          Logout
        </button>
      </div>

      <div className="admin-content">
        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-card-label">Total Issues</div>
              <div className="stat-card-value">{stats.total}</div>
            </div>
            <div className="stat-card open">
              <div className="stat-card-label">Open</div>
              <div className="stat-card-value">{stats.totalByStatus?.Open || 0}</div>
            </div>
            <div className="stat-card progress">
              <div className="stat-card-label">In Progress</div>
              <div className="stat-card-value">{stats.totalByStatus?.['In Progress'] || 0}</div>
            </div>
            <div className="stat-card resolved">
              <div className="stat-card-label">Resolved</div>
              <div className="stat-card-value">{stats.totalByStatus?.Resolved || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Avg Resolution</div>
              <div className="stat-card-value">{stats.avgResolutionTime || 0}d</div>
            </div>
          </div>
        )}

        {/* Issues Table */}
        <div className="admin-table-container">
          <div className="admin-table-header">
            <h2 style={{ margin: 0 }}>All Issues ({filteredIssues.length})</h2>
            <div className="admin-search">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search by ID, title, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIssues.map(issue => (
                  <tr key={issue.id} className={inlineEdit === issue.id ? 'updating' : ''}>
                    <td className="admin-table-id">{issue.id}</td>
                    <td className="admin-table-title">
                      <div className="title-cell">
                        <span className="title-text">{issue.title}</span>
                        {issue.commentCount > 0 && (
                          <span className="comment-badge">{issue.commentCount} 💬</span>
                        )}
                        {issue.upvotes > 0 && (
                          <span className="upvote-badge">{issue.upvotes} 👍</span>
                        )}
                      </div>
                    </td>
                    <td><span className="category-badge">{issue.category}</span></td>
                    <td>
                      <div className="inline-select-wrapper">
                        <select
                          className={`inline-status-select status-${issue.status?.toLowerCase().replace(' ', '-')}`}
                          value={issue.status}
                          onChange={(e) => handleQuickStatusChange(issue.id, e.target.value)}
                          disabled={inlineEdit === issue.id}
                        >
                          {STATUSES.map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                        <ChevronDown size={14} className="select-arrow" />
                      </div>
                    </td>
                    <td>
                      <div className="inline-select-wrapper">
                        <select
                          className="inline-priority-select"
                          value={issue.priority || 'Medium'}
                          onChange={(e) => handleQuickPriorityChange(issue.id, e.target.value)}
                          disabled={inlineEdit === issue.id}
                          style={{ color: getPriorityColor(issue.priority || 'Medium') }}
                        >
                          {PRIORITIES.map(priority => (
                            <option key={priority} value={priority}>{priority}</option>
                          ))}
                        </select>
                        <ChevronDown size={14} className="select-arrow" />
                      </div>
                    </td>
                    <td className="admin-table-date">{formatDate(issue.timestamp)}</td>
                    <td>
                      <div className="admin-table-actions">
                        <button 
                          className="admin-action-btn view"
                          onClick={() => navigate(`/issues/${issue.id}`)}
                          title="View Issue"
                        >
                          <Eye size={16} />
                        </button>
                        <button 
                          className="admin-action-btn edit"
                          onClick={() => setEditModal({ ...issue })}
                          title="Edit Details"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="admin-action-btn delete"
                          onClick={() => setDeleteConfirm(issue)}
                          title="Delete Issue"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredIssues.length === 0 && (
            <div className="empty-state">
              <p>No issues found</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="admin-modal-overlay" onClick={() => setEditModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div>
                <h2>Edit Issue</h2>
                <span className="modal-issue-id">{editModal.id}</span>
              </div>
              <button className="admin-modal-close" onClick={() => setEditModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="modal-issue-title">{editModal.title}</div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={editModal.status}
                    onChange={(e) => setEditModal(prev => ({ ...prev, status: e.target.value }))}
                  >
                    {STATUSES.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-select"
                    value={editModal.priority || 'Medium'}
                    onChange={(e) => setEditModal(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    {PRIORITIES.map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Assigned To</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Team member name"
                  value={editModal.assignedTo || ''}
                  onChange={(e) => setEditModal(prev => ({ ...prev, assignedTo: e.target.value }))}
                />
              </div>
              
              {(editModal.status === 'Resolved' || editModal.status === 'Closed') && (
                <div className="form-group">
                  <label className="form-label">Resolution Notes</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Describe how the issue was resolved..."
                    value={editModal.resolutionNotes || ''}
                    onChange={(e) => setEditModal(prev => ({ ...prev, resolutionNotes: e.target.value }))}
                    rows={3}
                  />
                </div>
              )}
              
              <div className="form-group">
                <label className="form-label">Admin Notes (Internal Only)</label>
                <textarea
                  className="form-textarea"
                  placeholder="Internal notes visible only to admins..."
                  value={editModal.adminNotes || ''}
                  onChange={(e) => setEditModal(prev => ({ ...prev, adminNotes: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="btn-secondary" onClick={() => setEditModal(null)}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={() => handleUpdateIssue(editModal.id, {
                  status: editModal.status,
                  priority: editModal.priority,
                  assignedTo: editModal.assignedTo,
                  resolutionNotes: editModal.resolutionNotes,
                  adminNotes: editModal.adminNotes
                })}
                disabled={updating}
              >
                {updating ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="admin-modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-content">
              <div className="delete-icon">
                <AlertCircle size={48} />
              </div>
              <h2>Delete Issue?</h2>
              <p className="delete-issue-id">{deleteConfirm.id}</p>
              <p className="delete-issue-title">"{deleteConfirm.title}"</p>
              <p className="delete-warning">
                This action cannot be undone. All comments and votes will also be deleted.
              </p>
              <div className="delete-modal-actions">
                <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </button>
                <button 
                  className="btn-danger"
                  onClick={() => handleDelete(deleteConfirm.id)}
                  disabled={updating}
                >
                  {updating ? 'Deleting...' : 'Delete Issue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
